/*
  Kiwi Ears · usage-slider background FX (Figma 221:717 + Rene's direction 2026-08-14).
  Three.js full-panel quad with a two-texture GLSL transition: directional soft wipe +
  counter-zoom, driven by GSAP on slide change (scroll-driven slider in pk-usage.liquid).
  Exposes window.kiwiUsageFX = { transitionTo(url, instant) }.
  Fallback: if WebGL is unavailable the panel keeps its static <img> background.
*/
import * as THREE from 'three';

(function () {
  var canvas = document.getElementById('pk-usage-gl');
  var panel = document.getElementById('pk-usage-panel');
  if (!canvas || !panel) return;

  var renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: false, alpha: false, powerPreference: 'low-power' });
  } catch (e) { return; } /* static <img> fallback stays visible */
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  renderer.setClearColor(0x090b16, 1);

  var scene = new THREE.Scene();
  var camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
  camera.position.z = 1;

  var uniforms = {
    uT1: { value: null },
    uT2: { value: null },
    uProg: { value: 0 },
    uRes: { value: new THREE.Vector2(1, 1) },
    uSize1: { value: new THREE.Vector2(1, 1) },
    uSize2: { value: new THREE.Vector2(1, 1) }
  };
  var material = new THREE.ShaderMaterial({
    uniforms: uniforms,
    vertexShader: 'varying vec2 vUv;void main(){vUv=uv;gl_Position=vec4(position.xy,0.,1.);}',
    fragmentShader: [
      'precision mediump float;',
      'varying vec2 vUv;',
      'uniform sampler2D uT1;uniform sampler2D uT2;',
      'uniform float uProg;uniform vec2 uRes;uniform vec2 uSize1;uniform vec2 uSize2;',
      /* object-cover UVs for arbitrary texture aspect */
      'vec2 cover(vec2 uv, vec2 size){',
      '  float ra=uRes.x/uRes.y, ia=size.x/size.y;',
      '  vec2 s=(ra<ia)?vec2(ia/ra,1.):vec2(1.,ra/ia);',
      '  return (uv-.5)/s+.5;',
      '}',
      'void main(){',
      '  float p=uProg;',
      /* soft directional wipe with a wavy edge + counter-zoom on both frames */
      '  float edge=smoothstep(p*1.5-.45+.12*sin(vUv.y*9.0+p*4.0), p*1.5+.05, vUv.x);',
      '  vec2 uvA=cover((vUv-.5)*(1.0+0.14*p)+.5, uSize1);',
      '  vec2 uvB=cover((vUv-.5)*(1.14-0.14*p)+.5, uSize2);',
      '  vec4 a=texture2D(uT1,uvA);',
      '  vec4 b=texture2D(uT2,uvB);',
      '  vec4 col=mix(b,a,edge);',
      /* Linear -> sRGB on the way out. Three tags these textures
         SRGBColorSpace, so on WebGL2 the GPU decodes them to linear when
         sampled — but a raw ShaderMaterial gets no output encode from the
         renderer, and the linear values were being written straight to the
         canvas. Measured result: the photo rendered at luminance 15.6 where
         the plain <img> fallback beside it read 53.9 — about 3.5x too dark
         (Rene 2026-08-30, "the photo looks very dark"). This is the exact
         sRGB transfer function rather than an approximate pow(1/2.2), so an
         untouched frame is pixel-identical to that <img>, and the mid-
         transition blend still happens in linear light where it belongs. */
      '  vec3 lo=col.rgb*12.92;',
      '  vec3 hi=1.055*pow(max(col.rgb,vec3(0.0)),vec3(1.0/2.4))-0.055;',
      '  gl_FragColor=vec4(mix(lo,hi,step(vec3(0.0031308),col.rgb)),col.a);',
      '}'
    ].join('\n'),
    depthTest: false,
    depthWrite: false
  });
  scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material));

  function resize() {
    var w = panel.clientWidth, h = panel.clientHeight;
    renderer.setSize(w, h, false);
    uniforms.uRes.value.set(w, h);
    render();
  }
  function render() { renderer.render(scene, camera); }

  var loader = new THREE.TextureLoader();
  var cache = {};
  function load(url) {
    return new Promise(function (res, rej) {
      if (cache[url]) return res(cache[url]);
      loader.load(url, function (t) {
        t.colorSpace = THREE.SRGBColorSpace;
        t.minFilter = THREE.LinearFilter;
        cache[url] = t; res(t);
      }, undefined, rej);
    });
  }

  var currentUrl = null, animating = false, queued = null;
  function transitionTo(url, instant) {
    if (!url || url === currentUrl) return;
    if (animating) { queued = url; return; }
    load(url).then(function (tex) {
      var sz = new THREE.Vector2(tex.image.width, tex.image.height);
      if (!uniforms.uT1.value) { /* first assignment — no transition */
        uniforms.uT1.value = tex; uniforms.uSize1.value.copy(sz);
        currentUrl = url; canvas.style.opacity = '1'; render(); return;
      }
      uniforms.uT2.value = tex; uniforms.uSize2.value.copy(sz);
      currentUrl = url;
      function done() {
        uniforms.uT1.value = uniforms.uT2.value;
        uniforms.uSize1.value.copy(uniforms.uSize2.value);
        uniforms.uProg.value = 0;
        render();
        animating = false;
        if (queued) { var q = queued; queued = null; transitionTo(q); }
      }
      if (instant || !window.gsap) { done(); return; }
      animating = true;
      window.gsap.fromTo(uniforms.uProg, { value: 0 }, {
        value: 1, duration: 1.05, ease: 'power2.inOut',
        onUpdate: render, onComplete: done
      });
    }).catch(function () { animating = false; });
  }

  if ('ResizeObserver' in window) new ResizeObserver(resize).observe(panel);
  else window.addEventListener('resize', resize);
  resize();

  window.kiwiUsageFX = { transitionTo: transitionTo };
  /* announce readiness so the orchestrator can seed the first frame */
  document.dispatchEvent(new CustomEvent('kiwi:usagefx-ready'));
})();
